/**
 * Spinal cannons — 3 manually-aimed heavy guns on the Mk IV hull. The player
 * locks an aim point; each cannon slowly turns to it (independently, but all on
 * the same point, so the shots land in a line). Firing launches twin big bolts
 * per cannon that do huge damage to moons/asteroids (obliterating small moons in
 * one hit) and heavy damage to ships.
 */
import { BIG_CANNON, CANNON, MKVI } from "@/config/balance";
import type { MineableBody } from "@/game/world/body";
import type { Damageable } from "./turrets";

const TEX_H = 512;

export interface CannonBullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

export interface CannonState {
  active: boolean; // a cannon is equipped in this slot
  mx: number; // mount world x
  my: number; // mount world y
  angle: number; // aim angle
  flash: number;
  cooldown: number;
}

export interface CannonCtx {
  shipX: number;
  shipY: number;
  heading: number;
  renderH: number; // rendered hull height (world units)
  equipped: (string | null)[]; // which slots have a cannon equipped
  aim: { x: number; y: number } | null;
  autoFire: boolean; // fire automatically once lined up (auto-combat / locked target)
  enemyTarget: boolean; // aim is an enemy ship → spread the bolts IN-LINE
  // Mining spread: when supplied (Battleship auto-mining, no enemy), each active
  // cannon locks a DIFFERENT rock (round-robin); one rock → all focus-fire it. This
  // OVERRIDES the shared `aim`.
  bodyTargets?: { x: number; y: number }[];
  bodies: MineableBody[]; // moons / asteroids
  enemies: Damageable[];
  mounts?: { ox: number; oy: number }[]; // per-path cannon mount offsets (default CANNON.mounts)
  dmgMul?: number; // Gun Commander buff — cannon damage
  fireMul?: number; // Gun Commander buff — cannon fire rate
  turnMul?: number; // Gun Commander buff — cannon turn rate
  onOreMined?: (amount: number) => void; // Battleship shoot-to-mine: credit carved ore as minerals
  onMoonKill: (b: MineableBody) => void; // a cannon destroyed this moon → big VFX
  onHit: (x: number, y: number, big: boolean) => void;
}

export class CannonSystem {
  // Sized to the max cannon count (Mk VI Battleship = 8); fewer-slot hulls leave the extras idle.
  readonly cannons: CannonState[] = Array.from({ length: MKVI.bts.cannons }, () => ({
    active: false,
    mx: 0,
    my: 0,
    angle: 0,
    flash: 0,
    cooldown: 0,
  }));
  readonly bullets: CannonBullet[] = [];
  private fireMul = 1; // Gun Commander fire-rate buff, applied in fireOne

  reset(): void {
    this.bullets.length = 0;
    for (const c of this.cannons) {
      c.flash = 0;
      c.cooldown = 0;
    }
  }

  update(dt: number, ctx: CannonCtx): void {
    const scale = ctx.renderH / TEX_H;
    const h = ctx.heading;
    const rx = Math.sin(h);
    const ry = -Math.cos(h);
    const fx = Math.cos(h);
    const fy = Math.sin(h);
    const mounts = ctx.mounts ?? CANNON.mounts;
    this.fireMul = ctx.fireMul ?? 1;
    // Mining spread: one rock per active cannon (round-robin); one rock → all focus it.
    const rocks = ctx.bodyTargets;
    const distribute = !!rocks && rocks.length > 0;
    let activeOrd = 0; // ordinal among ACTIVE cannons (for the rock round-robin)

    for (let i = 0; i < this.cannons.length; i++) {
      const c = this.cannons[i]!;
      const off = mounts[i];
      c.flash = Math.max(0, c.flash - dt * 5);
      c.cooldown -= dt;
      if (!off) {
        c.active = false; // no mount at this slot for the current hull
        continue;
      }
      c.mx = ctx.shipX + scale * (off.ox * rx + off.oy * fx);
      c.my = ctx.shipY + scale * (off.ox * ry + off.oy * fy);
      c.active = (ctx.equipped[i] ?? null) !== null;
      if (!c.active) continue; // empty slot — no cannon to aim
      let tx: number;
      let ty: number;
      let fireOK: boolean;
      if (distribute) {
        const r = rocks![activeOrd % rocks!.length]!;
        tx = r.x;
        ty = r.y;
        fireOK = true; // mining: fire once lined up
      } else {
        const aimPt = ctx.aim ?? { x: ctx.shipX + fx * 200, y: ctx.shipY + fy * 200 };
        tx = aimPt.x;
        ty = aimPt.y;
        fireOK = ctx.autoFire && !!ctx.aim;
      }
      activeOrd++;
      const desired = Math.atan2(ty - c.my, tx - c.mx);
      let diff = desired - c.angle;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      const step = CANNON.turnRate * (ctx.turnMul ?? 1) * dt;
      c.angle += diff > step ? step : diff < -step ? -step : diff;
      // Fire on its own once the barrel is lined up on target.
      if (fireOK && c.cooldown <= 0) {
        let post = desired - c.angle;
        while (post > Math.PI) post -= Math.PI * 2;
        while (post < -Math.PI) post += Math.PI * 2;
        if (Math.abs(post) < CANNON.fireCone) this.fireOne(c);
      }
    }

    // Advance bolts; a hit on a moon/asteroid does massive damage, a hit on a
    // ship does heavy damage.
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i]!;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
      let hit = false;
      for (const body of ctx.bodies) {
        if (body.ore <= 0) continue;
        const rr = body.radius + 6;
        if ((body.pos.x - b.x) ** 2 + (body.pos.y - b.y) ** 2 < rr * rr) {
          const before = body.ore;
          const removed = Math.min(before, CANNON.moonDamage * (ctx.dmgMul ?? 1));
          body.ore -= removed;
          ctx.onOreMined?.(removed); // Battleship shoot-to-mine credits the carved ore
          const isMoon = body.id.startsWith("moon_"); // main moons + small sub-moons
          if (body.ore <= 0 && before > 0 && isMoon) ctx.onMoonKill(body);
          ctx.onHit(b.x, b.y, true);
          hit = true;
          break;
        }
      }
      if (!hit) {
        for (const s of ctx.enemies) {
          if (s.hp <= 0) continue;
          const rr = 30;
          if ((s.pos.x - b.x) ** 2 + (s.pos.y - b.y) ** 2 < rr * rr) {
            this.damage(s, CANNON.boltDamage * (ctx.dmgMul ?? 1));
            ctx.onHit(b.x, b.y, false);
            hit = true;
            break;
          }
        }
      }
      if (hit || b.life <= 0) this.bullets.splice(i, 1);
    }
  }

  /** Force a volley from every ready cannon (the 🔥 button). */
  fire(): void {
    for (const c of this.cannons) {
      if (!c.active || c.cooldown > 0) continue;
      this.fireOne(c);
    }
  }

  /** One cannon fires twin big bolts side-by-side (parallel) — both leave the
   * barrels at the same instant, like the turrets, whatever the target. */
  private fireOne(c: CannonState): void {
    const dx = Math.cos(c.angle);
    const dy = Math.sin(c.angle);
    const px = -dy; // perpendicular
    const py = dx;
    for (let k = 0; k < CANNON.boltsPerShot; k++) {
      const s = k * 2 - (CANNON.boltsPerShot - 1); // -1, +1 for two bolts
      const ox = dx * 14 + px * (s * CANNON.barrelSpread);
      const oy = dy * 14 + py * (s * CANNON.barrelSpread);
      this.bullets.push({
        x: c.mx + ox,
        y: c.my + oy,
        vx: dx * CANNON.boltSpeed,
        vy: dy * CANNON.boltSpeed,
        life: CANNON.boltLife,
      });
    }
    c.cooldown = CANNON.fireInterval / this.fireMul;
    c.flash = 1;
  }

  private damage(s: Damageable, amount: number): void {
    let dmg = amount;
    if (s.shield > 0) {
      const a = Math.min(s.shield, dmg);
      s.shield -= a;
      dmg -= a;
    }
    if (dmg > 0) s.hp = Math.max(0, s.hp - dmg);
    s.shieldFlash = 1;
    s.hitTimer = 0;
  }
}

// ── BIG CANNONS ─────────────────────────────────────────────────────────────
// The Mk V Battleship's 2 centre-spine super-guns. They aim like the spinal
// cannons, then each barrel emits a persistent "kamehameha" BEAM that creeps out
// to reach clean across the field, tracks the aim, and deals CONTINUOUS damage
// to every ship/rock along its length until it fades.

/** Perp distance from point P to the ray segment [O → O+dir·len] (dir is unit). */
function distToSeg(
  px: number,
  py: number,
  ox: number,
  oy: number,
  dx: number,
  dy: number,
  len: number,
): number {
  let t = (px - ox) * dx + (py - oy) * dy;
  t = t < 0 ? 0 : t > len ? len : t;
  const cx = ox + dx * t;
  const cy = oy + dy * t;
  return Math.hypot(px - cx, py - cy);
}

/** A live beam segment, rebuilt every frame for the renderer. */
export interface BigBeam {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  t: number; // life progress 0 (just fired) → 1 (expiring), for the fade
}

export interface BigCannonState {
  active: boolean;
  mx: number;
  my: number;
  angle: number;
  flash: number;
  cooldown: number;
  beam: number; // seconds of beam remaining (>0 = firing)
  beamAge: number; // seconds since this beam volley started
}

export interface BigCannonCtx {
  shipX: number;
  shipY: number;
  heading: number;
  renderH: number;
  equipped: (string | null)[]; // which big-cannon slots hold a Big Cannon
  mounts: { ox: number; oy: number }[]; // BIG_CANNON centre-spine offsets
  // The Big Cannons target INDEPENDENTLY of the spinal cannons: each locks the
  // nearest enemy ship, aims straight at it, and fires on its own long cooldown.
  // On the Battleship the player can override with the aim/fire button:
  //   playerAim set → BOTH guns focus that point; playerAutoFire → fire on cooldown,
  //   else hold until the 🔥 button (fire()).
  enemies: Damageable[];
  bodies: MineableBody[];
  playerAim?: { x: number; y: number } | null;
  playerAutoFire?: boolean;
  dmgMul?: number; // Gun Commander buff
  fireMul?: number; // Gun Commander buff
  turnMul?: number; // Gun Commander buff
  beamMul?: number; // Mk VI Beam Capacitor upgrades — beam DURATION multiplier (default 1)
  // Mining targets to spread the beams across when no enemy is present: each gun
  // aims at a different in-range asteroid/moon (focus-fires when only one exists).
  bodyTargets?: { x: number; y: number }[];
  onOreMined?: (amount: number) => void; // Battleship shoot-to-mine credit
  onMoonKill: (b: MineableBody) => void;
  onHit: (x: number, y: number, big: boolean) => void;
}

export class BigCannonSystem {
  private beamDurMul = 1; // set each frame from ctx.beamMul; used by startBeam + the fire() button
  readonly guns: BigCannonState[] = Array.from({ length: BIG_CANNON.slots }, () => ({
    active: false,
    mx: 0,
    my: 0,
    angle: 0,
    flash: 0,
    cooldown: 0,
    beam: 0,
    beamAge: 0,
  }));
  readonly beams: BigBeam[] = []; // render descriptors, rebuilt every frame
  private sparkT = 0; // throttles impact sparks along the beam

  reset(): void {
    this.beams.length = 0;
    for (const g of this.guns) {
      g.flash = 0;
      g.cooldown = 0;
      g.beam = 0;
      g.beamAge = 0;
    }
  }

  update(dt: number, ctx: BigCannonCtx): void {
    const scale = ctx.renderH / TEX_H;
    const h = ctx.heading;
    const rx = Math.sin(h);
    const ry = -Math.cos(h);
    const fx = Math.cos(h);
    const fy = Math.sin(h);
    this.beams.length = 0;
    this.beamDurMul = ctx.beamMul ?? 1; // Mk VI Beam Capacitor upgrades
    this.sparkT -= dt;
    const spark = this.sparkT <= 0;
    if (spark) this.sparkT = 0.05;

    // In-range enemies, nearest first. The Big Cannons SPREAD their fire: the 1st
    // active gun takes the nearest, the 2nd the next nearest, etc. — but with only
    // one enemy in range, all lock onto it. With NO enemies, they spread across the
    // in-range asteroids/moons (mining) instead.
    const targets = this.targetsInRange(ctx);
    const rocks = ctx.bodyTargets ?? [];
    let activeN = 0; // ordinal of the active gun currently being processed

    for (let i = 0; i < this.guns.length; i++) {
      const g = this.guns[i]!;
      const off = ctx.mounts[i];
      g.flash = Math.max(0, g.flash - dt * 4);
      g.cooldown -= dt;
      if (!off) {
        g.active = false;
        g.beam = 0;
        continue;
      }
      g.mx = ctx.shipX + scale * (off.ox * rx + off.oy * fx);
      g.my = ctx.shipY + scale * (off.ox * ry + off.oy * fy);
      g.active = (ctx.equipped[i] ?? null) !== null;
      if (!g.active) {
        g.beam = 0;
        continue;
      }
      // Targeting: the player's aim (Battleship aim button) commands BOTH guns and
      // overrides the auto-target; otherwise each gun takes the (activeN)-th nearest
      // enemy so the two guns split targets.
      let aimX: number;
      let aimY: number;
      let canFire: boolean;
      if (ctx.playerAim) {
        aimX = ctx.playerAim.x;
        aimY = ctx.playerAim.y;
        canFire = ctx.playerAutoFire ?? false; // manual crosshair waits for the 🔥 button
      } else if (targets.length > 0) {
        // Combat: each gun takes the (activeN)-th nearest enemy (focus-fire if one).
        const enemy = targets[Math.min(activeN, targets.length - 1)]!;
        aimX = enemy.pos.x;
        aimY = enemy.pos.y;
        canFire = true;
      } else if (rocks.length > 0) {
        // Mining: each gun takes a DIFFERENT rock (focus-fire when only one exists).
        const r = rocks[activeN % rocks.length]!;
        aimX = r.x;
        aimY = r.y;
        canFire = true;
      } else {
        aimX = ctx.shipX + fx * 300;
        aimY = ctx.shipY + fy * 300;
        canFire = false;
      }
      activeN++;
      const desired = Math.atan2(aimY - g.my, aimX - g.mx);
      let diff = desired - g.angle;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      const step = BIG_CANNON.turnRate * (ctx.turnMul ?? 1) * dt;
      g.angle += diff > step ? step : diff < -step ? -step : diff;
      // Open fire once on target, lined up, and off the long cooldown.
      if (g.beam <= 0 && canFire && g.cooldown <= 0) {
        let post = desired - g.angle;
        while (post > Math.PI) post -= Math.PI * 2;
        while (post < -Math.PI) post += Math.PI * 2;
        if (Math.abs(post) < BIG_CANNON.fireCone) this.startBeam(g, ctx.fireMul ?? 1);
      }
      // A live beam: grow it, damage along it, and emit render segments.
      if (g.beam > 0) {
        g.beam -= dt;
        g.beamAge += dt;
        const len = BIG_CANNON.beamMaxLength * Math.min(1, g.beamAge / BIG_CANNON.beamGrowTime);
        const dx = Math.cos(g.angle);
        const dy = Math.sin(g.angle);
        const px = -dy;
        const py = dx;
        const tprog = 1 - Math.max(0, g.beam) / (BIG_CANNON.beamDuration * this.beamDurMul);
        // Emit from the muzzle: barrelLen ahead of the mount, ±barrelSpread abeam.
        for (const side of [-1, 1]) {
          const ox = g.mx + dx * BIG_CANNON.barrelLen + px * side * BIG_CANNON.barrelSpread;
          const oy = g.my + dy * BIG_CANNON.barrelLen + py * side * BIG_CANNON.barrelSpread;
          this.damageBeam(ox, oy, dx, dy, len, dt, ctx, spark);
          this.beams.push({ x0: ox, y0: oy, x1: ox + dx * len, y1: oy + dy * len, t: tprog });
        }
      }
    }
  }

  /** Shared 🔥 button: kick off a beam volley on every ready big cannon. */
  fire(fireMul = 1): void {
    for (const g of this.guns) {
      if (!g.active || g.cooldown > 0 || g.beam > 0) continue;
      this.startBeam(g, fireMul);
    }
  }

  private startBeam(g: BigCannonState, fireMul: number): void {
    g.beam = BIG_CANNON.beamDuration * this.beamDurMul; // Mk VI Beam Capacitor upgrades
    g.beamAge = 0;
    g.cooldown = BIG_CANNON.fireInterval / fireMul;
    g.flash = 1;
  }

  /** Live enemy ships within the Big Cannons' engage range (= the spinal cannons'
   * reach), sorted nearest-first around the hull. The two guns index into this so
   * they spread onto different targets when 2+ are in range. */
  private targetsInRange(ctx: BigCannonCtx): Damageable[] {
    const rSq = BIG_CANNON.targetRange * BIG_CANNON.targetRange;
    const out: Damageable[] = [];
    for (const e of ctx.enemies) {
      if (e.hp <= 0) continue;
      const dx = e.pos.x - ctx.shipX;
      const dy = e.pos.y - ctx.shipY;
      if (dx * dx + dy * dy <= rSq) out.push(e);
    }
    out.sort(
      (a, b) =>
        (a.pos.x - ctx.shipX) ** 2 +
        (a.pos.y - ctx.shipY) ** 2 -
        ((b.pos.x - ctx.shipX) ** 2 + (b.pos.y - ctx.shipY) ** 2),
    );
    return out;
  }

  private damageBeam(
    ox: number,
    oy: number,
    dx: number,
    dy: number,
    len: number,
    dt: number,
    ctx: BigCannonCtx,
    spark: boolean,
  ): void {
    const w = BIG_CANNON.beamWidth;
    for (const body of ctx.bodies) {
      if (body.ore <= 0) continue;
      if (distToSeg(body.pos.x, body.pos.y, ox, oy, dx, dy, len) > body.radius + w) continue;
      const before = body.ore;
      const removed = Math.min(before, BIG_CANNON.moonDps * (ctx.dmgMul ?? 1) * dt);
      body.ore -= removed;
      ctx.onOreMined?.(removed); // Battleship shoot-to-mine credit
      if (body.ore <= 0 && before > 0 && body.id.startsWith("moon_")) ctx.onMoonKill(body);
      if (spark) ctx.onHit(body.pos.x, body.pos.y, true);
    }
    for (const s of ctx.enemies) {
      if (s.hp <= 0) continue;
      if (distToSeg(s.pos.x, s.pos.y, ox, oy, dx, dy, len) > 30 + w) continue;
      this.damage(s, BIG_CANNON.dps * (ctx.dmgMul ?? 1) * dt);
      if (spark) ctx.onHit(s.pos.x, s.pos.y, false);
    }
  }

  private damage(s: Damageable, amount: number): void {
    let dmg = amount;
    if (s.shield > 0) {
      const a = Math.min(s.shield, dmg);
      s.shield -= a;
      dmg -= a;
    }
    if (dmg > 0) s.hp = Math.max(0, s.hp - dmg);
    s.shieldFlash = 1;
    s.hitTimer = 0;
  }
}
